import { useState } from 'react';
import { usePushNotification } from '@/context/PushNotificationContext';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { Bell, BellOff, BellRing, Check, X, Smartphone } from 'lucide-react';
import { cn } from '@/lib/utils';

export default function NotificationSettings() {
  const { permission, isSupported, requestPermission } = usePushNotification();
  const [isEnabling, setIsEnabling] = useState(false);

  const handleEnableNotifications = async () => {
    setIsEnabling(true);
    await requestPermission();
    setIsEnabling(false);
  };

  const getStatusIcon = () => {
    if (!isSupported) return <BellOff className="w-5 h-5 text-slate-400" />;
    if (permission === 'granted') return <BellRing className="w-5 h-5 text-green-500" />;
    if (permission === 'denied') return <BellOff className="w-5 h-5 text-red-500" />;
    return <Bell className="w-5 h-5 text-slate-400" />;
  };

  const getStatusText = () => {
    if (!isSupported) return 'Not Supported';
    if (permission === 'granted') return 'Enabled';
    if (permission === 'denied') return 'Blocked';
    return 'Not Enabled';
  };

  const getStatusColor = () => {
    if (!isSupported) return 'text-slate-500';
    if (permission === 'granted') return 'text-green-500';
    if (permission === 'denied') return 'text-red-500';
    return 'text-yellow-500';
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="ghost" 
          size="icon"
          className="relative"
          data-testid="notification-settings-btn"
        >
          {getStatusIcon()}
          {permission === 'granted' && (
            <span className="absolute top-1 right-1 w-2 h-2 bg-green-500 rounded-full" />
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72">
        <div className="px-3 py-2">
          <h4 className="font-semibold text-sm">Push Notifications</h4>
          <p className="text-xs text-slate-500 mt-1">
            Get notified for new enquiries and messages
          </p>
        </div>
        <DropdownMenuSeparator />
        
        <div className="px-3 py-2">
          <div className="flex items-center justify-between">
            <span className="text-sm">Status:</span>
            <span className={cn("text-sm font-medium", getStatusColor())}>
              {getStatusText()}
            </span>
          </div>
        </div>

        {!isSupported && (
          <div className="px-3 py-2 bg-slate-100 dark:bg-slate-800 mx-2 rounded-lg mb-2">
            <p className="text-xs text-slate-500">
              Push notifications are not supported in this browser. Try using Chrome, Firefox, or Edge.
            </p>
          </div>
        )}

        {isSupported && permission === 'default' && (
          <div className="px-3 py-2">
            <Button 
              onClick={handleEnableNotifications}
              disabled={isEnabling}
              className="w-full"
              size="sm"
              data-testid="enable-notifications-btn"
            >
              {isEnabling ? (
                <>
                  <span className="animate-spin mr-2">⏳</span>
                  Enabling...
                </>
              ) : (
                <>
                  <Bell className="w-4 h-4 mr-2" />
                  Enable Notifications
                </>
              )}
            </Button>
          </div>
        )}

        {isSupported && permission === 'denied' && (
          <div className="px-3 py-2 bg-red-50 dark:bg-red-900/20 mx-2 rounded-lg mb-2">
            <p className="text-xs text-red-600 dark:text-red-400">
              Notifications are blocked. Please enable them in your browser settings:
            </p>
            <ol className="text-xs text-red-500 mt-1 list-decimal list-inside">
              <li>Click the lock icon in the address bar</li>
              <li>Find "Notifications" setting</li>
              <li>Change to "Allow"</li>
            </ol>
          </div>
        )}

        {isSupported && permission === 'granted' && (
          <div className="px-3 py-2 bg-green-50 dark:bg-green-900/20 mx-2 rounded-lg mb-2">
            <div className="flex items-center gap-2 text-green-600 dark:text-green-400">
              <Check className="w-4 h-4" />
              <span className="text-xs font-medium">Notifications Active</span>
            </div>
            <p className="text-xs text-green-500 mt-1">
              You'll receive push notifications for:
            </p>
            <ul className="text-xs text-green-500 mt-1 list-disc list-inside">
              <li>New enquiries from website & AurousOrbit.ai</li>
              <li>New live chat messages</li>
            </ul>
          </div>
        )}

        <DropdownMenuSeparator />
        
        <div className="px-3 py-2 flex items-center gap-2 text-slate-500">
          <Smartphone className="w-4 h-4" />
          <span className="text-xs">Works on mobile browsers too!</span>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
